import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { arrayOf, bool, exact, func, shape, string } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { BootstrapTooltip, Icon, Switcher, TextArea } from '@atoms';
import { EditSocials, SearchListModal } from '@molecules';
import { TextInput } from '@atoms/Input';
import { PhotoChanger } from '@organisms';
import { Onboarding } from '@templates';
import { Typography, Flex } from '@mixins';
import { theme } from '@styles';
import { updateCompanyOnboarding } from '@store/company/duck';
import { routes } from '@constants';
import { ONBOARDING_STEPS } from '@constants/common';

import { ReactComponent as Info } from '@assets/svgs/Info.svg';

const CompanyProfileOnboarding = ({
  isCompanyProfileHidden,
  onCompanyProfileHiddenChangeState,
  direction,
  logo,
  onRemovePhoto,
  onSavePhoto,
  isPhotoLoading,
  onAboutUsSave,
  onIndustrySave,
  userIndustry,
  onWebsiteChange,
  aboutUs,
  website,
  industryList,
  socials,
  onSave,
  isProofOfPaymentStepAvailable,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [isIndustryModalOpen, setIsIndustryModalOpen] = useState(false);

  const handleIndustryModalChangeState = useCallback(() => {
    setIsIndustryModalOpen(prevValue => !prevValue);
  }, []);

  const handleIndustrySave = useCallback(newIndustry => {
    onIndustrySave(newIndustry);
    handleIndustryModalChangeState();
  }, []);

  const { user } = useSelector(store => store.userStore);

  const handleBack = () => {
    onSave();

    history.push(routes.scheduleOrientationOnboarding, { direction: 'back' });
  };

  const handleUpdateProfile = () => {
    onSave();
    dispatch(
      updateCompanyOnboarding({
        currentStep: ONBOARDING_STEPS.COMPANY_PROFILE,
      }),
    );
  };

  const handleNavigateNext = () => {
    history.push(isProofOfPaymentStepAvailable ? routes.proofOfPaymentOnboarding : routes.completeOnboarding, {
      direction: 'forward',
    });
  };

  const handleNext = () => {
    onSave();
    dispatch(
      updateCompanyOnboarding({
        currentStep: ONBOARDING_STEPS.COMPANY_PROFILE,
        ...(isProofOfPaymentStepAvailable && {
          nextStep: ONBOARDING_STEPS.PROOF_OF_PAYMENT,
        }),
      }),
    );

    handleNavigateNext();
  };

  return (
    <Onboarding
      onSaveAndContinueLater={handleUpdateProfile}
      onNext={handleNext}
      direction={direction}
      title="Company Profile"
      showNavigationItems
      currentStep={6}
      onBack={handleBack}>
      <Flex flexDirection="column" maxWidth={709}>
        <Typography
          variant="diatype-300"
          mb={30}
          fontSize={[14, 16, 20]}
          color={theme.color.mintMinded[900]}
          lineHeight="1.6">
          Update your company profile so other members can get to know you and the company. It will appear in our member
          application &quot;Know your Neighbor&quot; section.
        </Typography>
        <Flex alignItems="center" mb={20}>
          <BootstrapTooltip title="We will hide all your company profile details and team members." placement="bottom">
            <Flex>
              <Icon SVG={Info} width={20} height={20} />
            </Flex>
          </BootstrapTooltip>
          <Typography ml={10} mr={15} variant="diatype-400" fontSize={[12, 14]} color={theme.color.mintMinded[900]}>
            Keep Private
          </Typography>
          <Switcher isEnabled={isCompanyProfileHidden} onSwitch={onCompanyProfileHiddenChangeState} />
        </Flex>
        {!isCompanyProfileHidden && (
          <Flex width="100%">
            <Flex
              mr={['20px', '30px', '40px']}
              minWidth={[100, 150, 200]}
              width={[100, 150, 200]}
              flexDirection="column">
              <Flex mb="25px" height={[100, 150, 200]}>
                <PhotoChanger
                  isLoading={isPhotoLoading}
                  isEditable
                  avatar={logo}
                  isCompany
                  onSavePhoto={onSavePhoto}
                  onRemovePhoto={onRemovePhoto}
                />
              </Flex>
              <Typography color={theme.color.mintMinded[900]} variant="diatype-400" fontSize={[12, 14, 16]}>
                {user?.company?.name}
              </Typography>
            </Flex>
            <Flex width="100%" flexDirection="column" maxWidth={[220, 340, 480]}>
              <Typography lineHeight="1.2" mb={['9px', 12, 15]} variant="rightserif-400" fontSize={[12, 16, 20]}>
                About Us
              </Typography>
              <TextArea
                mb={[15, 18]}
                withBorder
                height="100px"
                width="100%"
                fontSize={[12, 14, 16]}
                value={aboutUs}
                onChange={onAboutUsSave}
                placeholder="Tell us about yourself"
              />
              <Typography lineHeight="1.2" mb={['8px', 10]} variant="rightserif-400" fontSize={[12, 16, 20]}>
                Industry
              </Typography>
              {userIndustry && (
                <Typography fontSize={[10, 14, 16]} variant="diatype-400" mb={['8px', 10]}>
                  {userIndustry}
                </Typography>
              )}
              <Typography
                pointer
                onClick={handleIndustryModalChangeState}
                fontSize={[10, 14, 16]}
                variant="diatype-400"
                borderBottom={`1px solid ${theme.color.gray[100]}`}
                color={theme.color.gray[200]}
                mb={[15, 22]}
                pb={['3px', '5px']}>
                {`+ Add${Object.keys(userIndustry ?? {}).length ? ' / Edit' : ''} industry`}
              </Typography>
              <Typography variant="rightserif-400" fontSize={[12, 16, 20]}>
                Website
              </Typography>
              <TextInput placeholder="www.companyname.com" value={website} onChange={onWebsiteChange} />
              <Typography mt={[15, 20]} variant="rightserif-400" fontSize={[12, 16, 20]}>
                Social Networks
              </Typography>
              <EditSocials isCompany={false} socials={socials} />
            </Flex>
          </Flex>
        )}
      </Flex>
      {isIndustryModalOpen && (
        <SearchListModal
          withMultipleSelection={false}
          onSelect={handleIndustrySave}
          title="Industry"
          itemsList={industryList}
          isOpen={isIndustryModalOpen}
          selectedItemsList={[userIndustry]}
          onModalClose={handleIndustryModalChangeState}
        />
      )}
    </Onboarding>
  );
};

CompanyProfileOnboarding.propTypes = {
  isProofOfPaymentStepAvailable: bool.isRequired,
  isCompanyProfileHidden: bool.isRequired,
  onCompanyProfileHiddenChangeState: func.isRequired,
  direction: string,
  logo: string,
  onRemovePhoto: func.isRequired,
  onSavePhoto: func.isRequired,
  isPhotoLoading: bool.isRequired,
  onWebsiteChange: func.isRequired,
  onAboutUsSave: func.isRequired,
  onIndustrySave: func.isRequired,
  onSave: func.isRequired,
  socials: arrayOf(
    exact({
      name: string,
      value: string,
      placeholder: string,
      handler: func,
      icon: shape({}),
    }),
  ).isRequired,
  aboutUs: string.isRequired,
  website: string.isRequired,
  industryList: arrayOf(string),
  userIndustry: string.isRequired,
};

CompanyProfileOnboarding.defaultProps = {
  direction: null,
  logo: '',
  industryList: [],
};

export default CompanyProfileOnboarding;
